import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, ChangeDetectionStrategy, signal, input, OnChanges, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Entity } from '../entity/entity';
import { FpaTaskData } from '../model/fpa-task-data';
import { PersonLogin, RoleName } from '../person/person-login';
import { EntityService } from '../services/entity-service';
import { IecTaskService } from '../services/iec-task-service';
import { PersonLoginService } from '../services/person-login-service';
import { StringUtils } from '../utils/string-utils';
import { DatePipe, CurrencyPipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FpaTaskService } from '../services/fpa-task.service';
import { FpaTaskResponse } from '../model/fpa-task-response';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-fpa-task-response',
  imports: [MatButton, MatIcon, MatProgressSpinner, DatePipe, CurrencyPipe, FormsModule],
  templateUrl: './fpa-task-response.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './fpa-task-response.component.css',
})
export class FpaTaskResponseComponent implements OnChanges, OnInit {
  private readonly taskId = signal<number>(undefined!);
  private readonly personLogin = signal<PersonLogin>(undefined!);
  private readonly taskService = inject(FpaTaskService);
  private readonly iecTaskService = inject(IecTaskService);
  private readonly personLoginService = inject(PersonLoginService);
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  readonly taskIdParam = input.required<string>({ alias: 'taskId' });
  readonly task = signal<FpaTaskData>(undefined!);
  readonly requestedMultiplier = signal<number>(undefined!);
  readonly comments = signal<string>(undefined!);
  readonly isBusy = signal(false);
  readonly errorMessage = signal<string>(undefined!);
  readonly taskMessage = signal<string>(undefined!);
  readonly entity = signal<Entity>(undefined!);
  readonly assignedTo = signal<string>(undefined!);

  private loadTaskDetails(): void {
    this.isBusy.set(true);
    this.initializeTaskData().then(
      () => (this.isBusy.set(false)),
      (error: HttpErrorResponse) => this.handleError(error),
    );
  }

  private async initializeTaskData(): Promise<void> {
    this.task.set(await firstValueFrom<FpaTaskData>(this.taskService.getTaskData(this.taskId())));
    if (!this.task()) {
      this.taskMessage.set('No task was found with ID ' + this.taskId());
      this.entity.set(undefined!);
      this.personLogin.set(undefined!);
    } else if (this.task().baseTask.endTime) {
      this.taskMessage.set('This task has been closed, you can close this window or use the links above to go to a different screen');
      this.task.set(undefined!);
      this.entity.set(undefined!);
      this.personLogin.set(undefined!);
    } else {
      this.assignedTo.set(await firstValueFrom<string>(this.iecTaskService.getAssignedTo(this.task().baseTask.assignee)));
      this.requestedMultiplier.set(this.task().requestedMultiplier);
      this.comments.set(this.task().comments);
      if (StringUtils.isNotBlank(this.task().baseTask.entityId)) {
        const entityPattern = new RegExp(/^(QT|MR|SR|R)\d+$/);
        if (entityPattern.test(this.task().baseTask.entityId)) {
          const entityName: string = this.task().baseTask.entityId.startsWith('QT') ? 'QUOTE' : 'CONTRACT';
          this.entity.set(await firstValueFrom<Entity>(this.entityService.getEntity(entityName, this.task().baseTask.entityId, this.task().baseTask.entityRevision)));
        }
      }
    }
  }

  canCloseTask(): boolean {
    if (!this.task()) {
      return false;
    }
    if (!this.personLogin()) {
      return false;
    }
    return this.isCloseAccessAllowed();
  }

  ngOnInit(): void {
    if (this.personLoginService.isOutsideEngineer()) {
      this.router.navigate(['ratings']).then(() => (this.isBusy.set(false)));
    } else {
      this.personLogin.set(this.personLoginService.getCachedLogin()!);
      this.initialize();
    }
  }

  closeTask(isApproved: boolean): void {
    if (this.task()) {
      if (!this.isCloseAccessAllowed()) {
        this.taskMessage.set('You do not have permission to close this task');
      }
      this.isBusy.set(true);
      const response = new FpaTaskResponse();
      response.taskId = this.taskId();
      response.responder = this.personLogin();
      response.approved = isApproved;
      response.requestedMultiplier = this.requestedMultiplier();
      response.comments = this.comments();
      this.taskService.closeTask(response).subscribe({
        next: () => {
          this.isBusy.set(false);
          this.loadTaskDetails();
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
  }

  private isCloseAccessAllowed() {
    if (!this.task()) {
      return true;
    }
    if (this.personLoginService.hasRole(RoleName.IT)) {
      return true;
    }
    return this.personLogin().token === this.task().baseTask.assignee || this.personLogin().groups.filter((group) => group.token === this.task().baseTask.assignee).length > 0;
  }

  getEntityUrl(): string {
    if (this.entity()) {
      const pageName: string = this.entity().id.entityName === 'QUOTE' ? 'quotes' : 'orders';
      return pageName + '/' + this.entity().id.entityId + '/' + this.entity().id.entityRevision;
    }
    return '';
  }

  private initialize(): void {
    const id: string = this.taskIdParam();
    if (id && !isNaN(+id)) {
      this.taskId.set(+id);
      this.loadTaskDetails();
    } else {
      this.taskMessage.set('Cannot find task information with an ID of ' + id);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['taskIdParam'].firstChange) {
      this.initialize();
    }
  }
}

